import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';

export type LoginCallbackContext = {
  client: 'electron';
  scheme: string;
  state?: string;
  redirect_uri?: string;
};

@Injectable({
  providedIn: 'root',
})
export class LoginCallbackService {
  private context: LoginCallbackContext | null = null;

  private readonly SCHEME_ALLOW_LIST = environment.production
    ? new Set(['accuzipdqclient', 'accuintellicore'])
    : new Set(['http', 'https', 'accuzipdqclient', 'accuintellicore']);

  /** Loopback hosts only — Electron local HTTP listeners. */
  private readonly TRUSTED_HOSTS = new Set([
    'localhost',
    '127.0.0.1',
    '[::1]',
  ]);

  /** Sets the context for the callback from the provided query params */
  setFromQueryParams(params: Record<string, string | undefined>): void {
    const client = params['client'];
    const scheme = params['scheme'];
    if (client === 'electron' && scheme) {
      if (!this.SCHEME_ALLOW_LIST.has(scheme)) {
        this.context = null;
        console.error(`Invalid scheme: ${scheme}`);
        throw new Error(`Invalid scheme: ${scheme}`);
      }

      const trustedScheme = scheme.replace(/:\/*$/, '');
      const rawRedirectUri = params['redirect_uri'];
      let redirect_uri: string | undefined;

      if (rawRedirectUri) {
        try {
          redirect_uri = decodeURIComponent(rawRedirectUri);
        } catch {
          this.context = null;
          console.error('Invalid redirect_uri encoding');
          throw new Error('Invalid redirect_uri encoding');
        }

        if (!this.isValidRedirectUri(redirect_uri, trustedScheme)) {
          this.context = null;
          console.error(`Invalid redirect_uri: ${redirect_uri}`);
          throw new Error(`Invalid redirect_uri: ${redirect_uri}`);
        }
      }

      this.context = {
        client: 'electron',
        scheme: trustedScheme,
        state: params['state'] ?? undefined,
        redirect_uri,
      };
    } else {
      this.context = null;
    }
  }

  /**
   * Ensures redirect_uri is valid. Accepts either:
   * - custom scheme matching the trusted Electron callback scheme, or
   * - http(s) with an allow-listed scheme and a trusted loopback host.
   */
  private isValidRedirectUri(
    redirectUri: string,
    trustedScheme: string
  ): boolean {
    let url: URL;
    try {
      url = new URL(redirectUri);
    } catch {
      return false;
    }

    const protocol = url.protocol.replace(/:$/, '').toLowerCase();
    if (!this.SCHEME_ALLOW_LIST.has(protocol)) {
      return false;
    }

    if (protocol === 'http' || protocol === 'https') {
      return this.TRUSTED_HOSTS.has(url.hostname.toLowerCase());
    }

    // Custom protocol: bind to the already-validated Electron scheme.
    return protocol === trustedScheme.toLowerCase();
  }

  getContext(): LoginCallbackContext | null {
    return this.context;
  }

  hasElectronContext(): boolean {
    return this.context !== null;
  }

  clear(): void {
    this.context = null;
  }
}
